import { Button, Checkbox, ListItemText, Chip, DialogTitle, TextField, Dialog, Tooltip as MuiTooltip, IconButton, DialogContent } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useSelector } from 'react-redux';
import React, { useState, useEffect, useRef } from "react";
import { selectMainTheme } from "app/store/fuse/settingsSlice";
import { ThemeProvider } from "@material-ui/core/styles";
import { TbWaveSine } from "react-icons/tb";
import { PiSteps } from "react-icons/pi";
import { useSipWebsocket } from 'app/protocol/SipWebsocketProvider';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import DeviceEditService from "../service/DeviceEditService";
import deviceControl from '../service/DeviceService';

const intervalButtons = [
    { label: 'All', unit: null },
    { label: '1 min', unit: { min: 1 } },
    { label: '2 mins', unit: { min: 2 } },
    { label: '3 mins', unit: { min: 3 } },
    { label: '4 mins', unit: { min: 4 } },
    { label: '5 mins', unit: { min: 5 } },
    { label: '10 mins', unit: { min: 10 } },
    { label: '20 mins', unit: { min: 20 } },
    { label: '30 mins', unit: { min: 30 } },
    { label: '60 mins', unit: { min: 60 } },
];

export default function StreamingPopup(props) {
    const { message } = useSipWebsocket();

    const [fullData, setFullData] = useState([]);

    const [selectedUnits, setSelectedUnits] = useState([]);
    const [streamingInProgress, setStreamingInProgress] = useState(false);
    const [uom, setUom] = useState(null);
    const [deviceData, setDeviceData] = useState({});
    const [selectedSensor, setSelectedSensor] = useState('');
    const [unitMapping, setUnitMapping] = useState([]);
    const [showAllInOneChart, setShowAllInOneChart] = useState(true);
    const [loadingData, setLoadingData] = useState(false);
    const [sensorName, setSensorName] = useState('');
    const [lineType, setLineType] = useState('monotone');
    const [selectedInterval, setSelectedInterval] = useState(intervalButtons[0]);
    const isMounted = useRef(true);

    const mainTheme = useSelector(selectMainTheme);

    const MIN_SEPARATION = 36;
    const COLOR_CAPACITY = Math.floor(360 / MIN_SEPARATION);
    const SAT = 80;
    const LIGHT = 45;

    const DASH_PATTERNS = [
        '0',         // solid
        '6 4',       // dash
        '4 4',       // short dash
        '3 6',       // dot
        '6 4 3 4',   // dash-dot
    ];

    const PALETTE = React.useMemo(
        () =>
            Array.from({ length: COLOR_CAPACITY }, (_, i) => {
                const hue = Math.round(((i + 0.5) * 360) / COLOR_CAPACITY) % 360;
                const sat = SAT + (((i * 7) % 3) - 1) * 4;
                const light = LIGHT + (((i * 11) % 3) - 1) * 4;
                return `hsl(${hue}, ${sat}%, ${light}%)`;
            }),
        []
    );

    const allSeriesKeys = React.useMemo(() => {
        const s = new Set();
        fullData.filter(d => d.deviceName === props.item.name).forEach(d => s.add(`${d.unit}-${d.sid}-${d.vid}`));

        unitMapping.forEach(m => {
            if (m.valueId) s.add(`${m.unit}-${m.sensorId}-${m.valueId}`);
            if (m.valueKey) s.add(`${m.unit}-${m.sensorId}-${m.valueKey}`);
        });

        return Array.from(s).sort((a, b) => a.localeCompare(b));
    }, [fullData, unitMapping, props.item?.name]);

    const stylesByKey = React.useMemo(() => {
        const m = new Map();
        allSeriesKeys.forEach((k, i) => {
            const color = PALETTE[i % COLOR_CAPACITY];
            const tier = Math.floor(i / COLOR_CAPACITY) % DASH_PATTERNS.length;
            m.set(k, { stroke: color, dash: DASH_PATTERNS[tier] });
        });
        return m;
    }, [allSeriesKeys, PALETTE]);

    const handleIntervalChange = (interval) => {
        setSelectedInterval(interval);
    };

    const IntervalButtons = () => (
        <div className='flex gap-4 items-center'>
            {intervalButtons.map(({ label, unit }) => (
                <MuiTooltip key={label} title={(unit ? 'Within ' : '') + label} arrow>
                    <span>
                        <IconButton
                            size='small'
                            onClick={() => handleIntervalChange({ label, unit })}
                            disabled={selectedInterval.label === label}
                            className={`rounded-full border ${selectedInterval.label === label
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'hover:bg-gray-100 border-gray-300'
                                }`}
                        >
                            <span className='text-xs font-semibold p-4'>{label}</span>
                        </IconButton>
                    </span>
                </MuiTooltip>
            ))}

            <MuiTooltip title='Toggle line type' arrow>
                <span>
                    <IconButton size='small' onClick={() => setLineType(prev => prev === 'monotone' ? 'step' : 'monotone')}>
                        {lineType === 'monotone' ? <PiSteps size={20} /> : <TbWaveSine size={20} />}
                    </IconButton>
                </span>
            </MuiTooltip>
        </div>
    );

    const getDisplayData = () => {
        console.log('fullData sample:', fullData.slice(-5));
        if (!selectedInterval.unit || fullData.length === 0) return fullData.filter(d => d.deviceName === props.item.name);

        const latestTime = new Date(fullData[fullData.length - 1].time);
        const { min } = selectedInterval.unit;
        const earliestTime = new Date(latestTime.getTime() - min * 60 * 1000);

        fullData.slice(-5).forEach(d => {
            console.log('d.time:', d.time, 'parsed:', new Date(d.time));
        });

        return fullData.filter(d => {
            const dTime = new Date(d.time);
            return dTime >= earliestTime && dTime <= latestTime && d.deviceName === props.item.name;
        });
    };

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        onOpen();
    }, []);

    useEffect(() => {
        if (message && streamingInProgress && /*typeof message === 'object' &&*/ message.type !== 'PONG' && message.payload.deviceName === props.item.name) {
            const formattedData = message.payload.measurementData.data.map(data => {
                const formatted = formatTime(data.time);
                console.log('Raw time:', data.time, 'Formatted:', formatted);
                return {
                    ...data,
                    time: formatted,
                    deviceName: message.payload.deviceName,
                };
            });

            setFullData(prevData => [...prevData, ...formattedData]);
        }
    }, [message, streamingInProgress]);

    useEffect(() => {
        if (streamingInProgress && uom) {
            console.log('Streaming in progress and UOM is set. Ready to display chart.');
        }
    }, [streamingInProgress, uom]);

    useEffect(() => {
        if (!deviceData.deviceType) return;

        const mapping = [];

        deviceData.deviceType.sensorTypeList.forEach(sensorType => {
            sensorType.valueList.forEach(value => {
                const sensorConfig = deviceData.sensorConfigs.find(
                    config => config.sensorExternalId === sensorType.externalId
                );
                const sensor = sensorConfig?.name || sensorType.name;

                mapping.push({
                    name: `${sensor}.${value.name}`,
                    unit: value.unit || 'N/A',
                    sensorId: sensorType.externalId,
                    valueId: value.externalId,
                    valueKey: value.name,
                });
            });
        });

        setUnitMapping(mapping);
    }, [deviceData]);


    useEffect(() => {
        if (unitMapping.length > 0 && selectedUnits.length === 0) {
            setSelectedUnits(unitMapping);
        }
    }, [unitMapping]);

    const formatTime = (timeString) => {
        const [date, time] = timeString.split(' ');
        const [yy, mm, dd] = date.split('/');
        const yyyy = Number(yy) < 100 ? `20${yy}` : yy;
        return `${yyyy}-${mm}-${dd} ${time.split('.')[0]}`;
    };

    const onOpen = () => {
        setFullData([]);
        setSelectedUnits([]);
        setSelectedSensor('');
        setUnitMapping([]);
        setShowAllInOneChart(true);
        setLoadingData(true);
        setSelectedInterval(intervalButtons[0]);

        deviceControl.findById(props.item.id).then(data => {
            setDeviceData(data);

            const firstSensor = data.deviceType.sensorTypeList[0];
            if (firstSensor) {
                setSelectedSensor(firstSensor.externalId);
                setSensorName(firstSensor.name);
            }

            if (props.startStreaming) {
                DeviceEditService.startStreaming(props.item.id).then(resp => {
                    setStreamingInProgress(true);
                });
            }
            else {
                setStreamingInProgress(true);
            }
        });
    };

    const onClose = () => {
        props.onClose();
        DeviceEditService.stop(props.item.id).then(resp => {
            if (!isMounted.current) return;
            setSelectedInterval(intervalButtons[0]);
            setStreamingInProgress(false);
            setFullData([]);
            setSelectedUnits([]);
            setSelectedSensor('');
            setUnitMapping([]);
        });
    };

    const renderLineChart = (unitObjs) => {
        const allData = getDisplayData();

        const unitKeySet = new Set(
            (Array.isArray(unitObjs) ? unitObjs : [unitObjs])
                .map(u => `${u.sensorId}-${u.valueId}`)
        );

        const filteredData = allData.filter(d =>
            unitKeySet.has(`${d.sid}-${d.vid}`)
        );

        const sensorNameLookup = {};
        deviceData.deviceType.sensorTypeList.forEach(sensor => {
            const config = deviceData.sensorConfigs.find(
                (cfg) => cfg.sensorExternalId === sensor.externalId
            );
            sensorNameLookup[sensor.externalId] = config?.name || sensor.name;
        });

        const dataMap = {};
        filteredData.forEach(data => {
            if (!dataMap[data.time]) {
                dataMap[data.time] = {};
            }
            const sensorValueName = `${data.unit}-${data.sid}-${data.vid}`;
            dataMap[data.time][sensorValueName] = data.val;
        });

        const allKeys = new Set();
        filteredData.forEach(data => {
            const sensorValueName = `${data.unit}-${data.sid}-${data.vid}`;
            allKeys.add(sensorValueName);
        });

        Object.keys(dataMap).forEach(time => {
            allKeys.forEach(key => {
                if (!Object.prototype.hasOwnProperty.call(dataMap[time], key)) {
                    dataMap[time][key] = null;
                }
            });
        });

        const chartData = Object.keys(dataMap).map(time => ({
            time,
            ...dataMap[time],
        }));

        const valueTypes = Array.from(allKeys)
            .map(key => {
                const [unitName, sid, vid] = key.split('-');
                return { key, unitName, sid, vid, sensorName: sensorNameLookup[sid] || sid };
            })
            .sort((a, b) => {
                if (a.sensorName !== b.sensorName) return a.sensorName.localeCompare(b.sensorName);
                if (a.unitName !== b.unitName) return a.unitName.localeCompare(b.unitName);
                return a.vid.localeCompare(b.vid);
            })
            .map(x => x.key);

        const CustomizedDot = React.memo(({ cx, cy, payload }) => {
            if (payload.outlier) return <circle cx={cx} cy={cy} r={5} fill='red' stroke='white' strokeWidth={1.5} />;
            return null;
        });

        const HoverDot = React.memo(({ cx, cy }) => (
            <circle cx={cx} cy={cy} r={4} fill='#252f3e' stroke='white' strokeWidth={1.5} />
        ));

        return (
            <div key={unitObjs.name} style={{
                borderRadius: '15px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                padding: '20px',
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '20px',
            }}>
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={chartData} margin={{ top: 10, right: 16, bottom: 56, left: 12 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis
                            domain={[0, dataMax => Math.max(10, dataMax * 1.1)]}
                            tickFormatter={(value) => Math.round(value)}
                        />
                        <Tooltip />
                        <Legend
                            verticalAlign="bottom"
                            align="center"
                            layout="horizontal"
                            wrapperStyle={{ paddingTop: 8, maxWidth: '100%', whiteSpace: 'normal', lineHeight: '18px' }}
                            height={48}
                            content={() => (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', paddingTop: 8 }}>
                                    {valueTypes.map((valueType) => {
                                        const st = stylesByKey.get(valueType) || { stroke: '#8884d8', dash: '0' };
                                        const [unitName, sensorId] = valueType.split('-');
                                        const label = `${unitName} (${sensorNameLookup[sensorId] || sensorId})`;
                                        return (
                                            <div key={valueType} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <svg width="26" height="12" aria-hidden>
                                                    <line x1="0" y1="6" x2="26" y2="6" stroke={st.stroke} strokeWidth="3" strokeDasharray={st.dash} />
                                                </svg>
                                                <span style={{ fontSize: 12 }}>{label}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        />
                        {valueTypes.map((valueType) => {
                            const st = stylesByKey.get(valueType) || { stroke: '#8884d8', dash: '0' };
                            const { stroke, dash: baseDash } = st;
                            const single = valueTypes.length === 1;

                            const dash = single ? '0' : baseDash;

                            return (
                                <Line
                                    key={valueType}
                                    type={lineType}
                                    dataKey={valueType}
                                    stroke={stroke}
                                    strokeDasharray={dash}
                                    strokeWidth={2}
                                    name={`${valueType.split('-')[0]} (${sensorNameLookup[valueType.split('-')[1]] || valueType.split('-')[1]})`}
                                    dot={<CustomizedDot />}
                                    activeDot={<HoverDot />}
                                    isAnimationActive={false}
                                    connectNulls
                                />
                            );
                        })}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    useEffect(() => {
        if (fullData.length > 0) {
            console.log('Checking data availability for selected sensor:', selectedSensor);
            console.log('FullData latest:', fullData.slice(-5));

            const sameDeviceData = fullData.some(data => data.deviceName === props.item.name);
            if (sameDeviceData) {
                console.log('Data found for current device. Turning off loading.');
                setLoadingData(false);
            }
        }
    }, [fullData, selectedSensor]);

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog open fullWidth maxWidth={fullData.length ? "lg" : "sm"} onClose={onClose}>
                <DialogTitle disableTypography className="border-b flex flex-col px-0">
                    <div className={`flex items-center justify-between gap-32 px-32 ${fullData.length ? 'border-b pb-14' : ''}`}>
                        <div className="whitespace-nowrap">
                            {props.title ?? `Timeseries${props.name ? ` data for ${props.name}` : ''}`}
                        </div>
                        <Button onClick={onClose}>
                            Close
                        </Button>
                    </div>
                    <div className="w-full flex flex-col gap-8 shadow-inner px-32 pt-14">
                        <div className="w-full flex flex-col gap-8 justify-between rounded-xl bg-gray-100 py-10 shadow px-16">
                            <div className="flex justify-between items-center gap-8">
                                <Autocomplete
                                    className="flex-1"
                                    multiple
                                    disableCloseOnSelect
                                    options={unitMapping}
                                    getOptionLabel={(option) => `${option.name} (${option.sensorId})`}
                                    value={selectedUnits}
                                    onChange={(e, newValue) => setSelectedUnits(newValue)}
                                    renderOption={(option, { selected }) => (
                                        <>
                                            <Checkbox checked={selected} style={{ marginRight: 8 }} />
                                            <ListItemText primary={option.name} />
                                        </>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                key={`${option.sensorId}-${option.valueId}`}
                                                label={option.name}
                                                {...getTagProps({ index })}
                                                size="small"
                                                style={{ marginRight: 4, marginBottom: 4 }}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Filter units"
                                            placeholder="Select units to display"
                                        />
                                    )}
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={unitMapping.length === 0}
                                    onClick={() => {
                                        if (selectedUnits.length === unitMapping.length) {
                                            setSelectedUnits([]);
                                        } else {
                                            setSelectedUnits([...unitMapping]);
                                        }
                                    }}
                                >
                                    {selectedUnits.length === unitMapping.length ? 'Deselect All' : 'Select All'}
                                </Button>
                            </div>

                            <IntervalButtons />
                        </div>
                    </div>
                </DialogTitle>
                <DialogContent className="w-full flex flex-col p-20" style={{ height: getDisplayData().length ? '520px' : 'auto', overflow: 'auto' }}>
                    {loadingData ? (
                        <div className='flex justify-center items-center text-center text-lg font-semibold text-gray-500 py-20'>
                            Loading data...
                        </div>
                    ) : getDisplayData().length === 0 ? (
                        <div className='text-center'>No data available</div>
                    ) : (
                        <div className="w-full flex-1" style={{ minHeight: 400 }}>
                            {showAllInOneChart
                                ? renderLineChart(selectedUnits)
                                : selectedUnits.map(unitObj => renderLineChart(unitObj))
                            }
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
}